import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import compose from 'lodash/flowRight'

import {breadcrumbsStore} from 'appBase/TopNav'

import useSmartNavigate from 'hooks/useSmartNavigate'

import withUriParams from 'hocs/withUriParams'
import withInitialDataLoadWaiting from 'hocs/withInitialDataLoadWaiting'
import withGeneratorDataSource from 'hocs/withGeneratorDataSource'

import GeneratorBlock from 'components/fieldBlocks/GeneratorBlock'
import SaveButtonsBlock from 'components/blocks/SaveButtonsBlock'
import NamedBlock from 'components/blocks/NamedBlock'
import ListWithSort from 'components/blocks/ListWithSort'
import PanelModal from 'components/modals/PanelModal'

import {queryServiceDevices} from '../data/serviceQueries'
import {createDevice, updateDevice, deleteDevice} from '../data/serviceMutations'

import entityUtils from 'utils/entityUtils'
import {createMlJSONStringWithCurrentLanguage} from 'utils/translationUtils'

import serviceStaticPrototypes from '../data/serviceStaticPrototypes'
import {DEVICE, SERVICE_DEVICES_VIEW} from 'constants/constants'

const DeviceView = compose(
     withUriParams([['deviceId', Number]]),
     queryServiceDevices,
     createDevice, updateDevice, deleteDevice,
     withGeneratorDataSource,
     withInitialDataLoadWaiting(['serviceDevices'])
)((props) => {

    const {deviceId, serviceDevices, createDevice, updateDevice, deleteDevice,
           dataSource, setDataSource, saveAndReset, isChanged, hasErrors, onChange, onValidate} = props;
    const navigate = useSmartNavigate();
    const {t, i18n} = useTranslation();

    const deviceProto = serviceStaticPrototypes[DEVICE];
    const dbDeviceDataMapper = (d) => {
        if (!d) {
            return null;
        }

        return {
            id: d.id,
            nameMl: d.nameMl,
            descriptionMl: d.descriptionMl,
            ...entityUtils.safeJSONParse(d.parameters)
        };
    };

    // device object for dataSource
    const deviceSource = useMemo(() => {
        const device = deviceId && serviceDevices.find(device => device.id === deviceId);
        return dbDeviceDataMapper(device);
    },
    [deviceId, serviceDevices]);


    // setting dataSource
    useEffect(() => {
        setDataSource(
            deviceSource,
            (data) => {
                const {id, nameMl, descriptionMl, ...parameters} = data;
                const device = {
                    id: id,
                    nameMl: nameMl,
                    descriptionMl: descriptionMl,
                    parameters: JSON.stringify(parameters)
                };
                updateDevice(device);
        });
    }, [setDataSource, deviceSource, updateDevice]);

    // preparing links
    breadcrumbsStore.register(SERVICE_DEVICES_VIEW);
    useEffect(() => {
        breadcrumbsStore.set(SERVICE_DEVICES_VIEW, {name: t('appService.devices'), to: `${props.uri}`}, true);
    });

    // rows
    const rows = serviceDevices.map(device => ({
        fields: [
            device.name,
            device.description,
            device.createdAt && device.createdAt.substring(0, 10),
            device.modifiedAt && device.modifiedAt.substring(0, 10)],
        actions: {
            "select": () => navigate(`${props.uri}/${device.id}${props.location.search}`),
            "removeConfirmation": () => deleteDevice(device.id)
         }
    }));

    rows.push({
        fields: [t('appService.views.DeviceView.createNewDevice')],
        className: "text-end",
        notSelectable: true,
        actions: {
            "click": () => createDevice({
                nameMl: createMlJSONStringWithCurrentLanguage(t('appService.views.DeviceView.newDevice'), i18n)
            }).then(({data: {createDevice: { id }}}) => {
                navigate(`${props.uri}/${id}${props.location.search}`);
            })
        }
    });

    //onSave, onCancel
    const onSave = () => {
        saveAndReset();
        navigate(`${props.uri}${props.location.search}`);
    };
    const onCancel = () => {
        setDataSource(null);
        navigate(`${props.uri}${props.location.search}`);
    };

    return (
        <div className="d-flex flex-column h-100 w-100">
            <div className="flex-grow d-flex flex-column scroll-parent">
                <div className="flex-grow d-flex scroll-parent">
                    <div className="flex-grow scroll">
                        <h5 className="text-center mt-3">{t('appService.devices')}</h5>
                        <div className="container">
                            <NamedBlock>
                                <ListWithSort headers={[t('appService.views.DeviceView.name'),
                                                        t('appService.views.DeviceView.description'),
                                                        t('appService.views.DeviceView.createdAt'),
                                                        t('appService.views.DeviceView.modifiedAt')]}
                                              sizes={["145*", "260*", "50*", "50*"]}
                                              rows={rows} />
                            </NamedBlock>

                            {dataSource &&
                            <PanelModal show title={t('appService.views.DeviceView.editDevice')} noPadding scrollable onClose={onCancel}>
                                <SaveButtonsBlock isChanged={isChanged} hasErrors={hasErrors} onSave={onSave} onCancel={onCancel} />

                                <div className="scroll flex-grow d-flex flex-column workpanel-gray">
                                    <GeneratorBlock data={dataSource} onChange={onChange} onValidate={onValidate} wide
                                        items={deviceProto}/>
                                </div>
                            </PanelModal> }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default DeviceView;